import { alpha, Box, Button, Divider, ListItemText, Link as MuiLink, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import Markdown from 'react-markdown'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import * as XLSX from 'xlsx'
import CsvIcon from '../../images/icons/CsvIcon'
import ExcelIcon from '../../images/icons/ExcelIcon'
import AIRoadmapResponse from '../RoadmapBuilder/AIRoadmapResponse'
import DealCard from './DealCard'
import StepFormRenderer from './StepFormRenderer'

interface AgentMarkdownProps {
  content: string
  onAction?: (text: string) => void
  onNavigate?: (pathname: string) => void
  deals?: any[]
  organizations?: any[]
  leads?: any[]
  isInteractive?: boolean
}

interface ActionButton {
  label: string
  action: string
}

// Extract [Label](action:message) patterns from the content
// and return the cleaned content + extracted buttons
function extractActions(content: string): { cleanContent: string; actions: ActionButton[] } {
  const actionRegex = /\[([^\]]+)\]\(action:([^)]+)\)/g
  const actions: ActionButton[] = []
  let match: RegExpExecArray | null

  while ((match = actionRegex.exec(content)) !== null) {
    actions.push({ label: match[1], action: decodeURIComponent(match[2]) })
  }

  // Remove action links from the content
  // Also strip trailing "— text" descriptions after deal links (the card shows these details)
  const cleanContent = content
    .replace(actionRegex, '')
    .replace(/(\[[^\]]+\]\(\/deals\/[^)]+\))\s*[\u2014\u2013-]\s*[^\n]*/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  return { cleanContent, actions }
}

/** Parse markdown GFM tables into structured data for export */
function parseMarkdownTables(content: string): Array<{ headers: string[]; rows: string[][] }> {
  const lines = content.split('\n')
  const tables: Array<{ headers: string[]; rows: string[][] }> = []
  let i = 0

  const parseRow = (line: string) =>
    line
      .split('|')
      .map(c => c.trim())
      .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)

  while (i < lines.length) {
    const line = lines[i].trim()
    if (line.startsWith('|') && i + 1 < lines.length && /^\|[-: |]+\|$/.test(lines[i + 1].trim())) {
      const headers = parseRow(lines[i])
      i += 2 // skip header + separator
      const rows: string[][] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(parseRow(lines[i]))
        i++
      }
      if (headers.length > 0) tables.push({ headers, rows })
    } else {
      i++
    }
  }

  return tables
}

function downloadCsv(tableData: { headers: string[]; rows: string[][] }, filename: string) {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`
  const csv = [tableData.headers, ...tableData.rows].map(row => row.map(esc).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function downloadXlsx(tableData: { headers: string[]; rows: string[][] }, filename: string) {
  const ws = XLSX.utils.aoa_to_sheet([tableData.headers, ...tableData.rows])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Export')
  XLSX.writeFile(wb, `${filename}.xlsx`)
}

const AgentMarkdown: React.FC<AgentMarkdownProps> = ({ content, onAction, onNavigate, deals, organizations, leads, isInteractive }) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const { cleanContent, actions } = useMemo(() => extractActions(content), [content])
  const parsedTables = useMemo(() => parseMarkdownTables(cleanContent), [cleanContent])
  // Increments during each render pass so each <table> gets its matching parsed data
  let tableRenderIndex = 0

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <Typography variant="body1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, textAlign: 'left' }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mt: 1.25, mb: 0.5, fontSize: '0.875rem', textAlign: 'left' }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 1, mb: 0.25, textAlign: 'left' }}>
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mt: 0.75, mb: 0.25, color: 'text.secondary', textAlign: 'left' }}
            >
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography
              component="div"
              variant="body2"
              sx={{ mb: 0.75, lineHeight: 1.7, color: 'text.primary', textAlign: 'left' }}
            >
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {children}
            </Box>
          ),
          em: ({ children }) => <em>{children}</em>,
          ul: ({ children }) => (
            <Box
              component="ul"
              sx={{
                pl: 0,
                mb: 0.75,
                mt: 0.25,
                listStyleType: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                '& li': { mb: 0.25 },
              }}
            >
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box
              component="ol"
              sx={{
                pl: 2,
                mb: 0.75,
                mt: 0.25,
                '& li': { mb: 0.25 },
                '& li::marker': { color: 'text.secondary', fontWeight: 600 },
              }}
            >
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <ListItemText
              // component="li"
              // variant="body2"
              sx={{
                lineHeight: 1.6,
                textAlign: 'left',
                color: theme.palette.text.ai_agent_text,
              }}
            >
              {children}
            </ListItemText>
          ),
          hr: () => <Divider sx={{ my: 1.5, opacity: 0.5 }} />,
          code: ({ children, className }) => {
            // Interactive step forms
            if (className?.includes('language-stepform')) {
              try {
                const rawText = typeof children === 'string'
                  ? children
                  : Array.isArray(children)
                    ? children.map(c => (typeof c === 'string' ? c : '')).join('')
                    : String(children ?? '')
                const config = JSON.parse(rawText.trim())
                return (
                  <StepFormRenderer
                    config={config}
                    onAction={onAction!}
                    deals={deals ?? []}
                    organizations={organizations ?? []}
                    leads={leads ?? []}
                    isInteractive={isInteractive ?? false}
                  />
                )
              } catch {
                // Fall through to generic code block on parse failure
              }
            }

            // AI-generated roadmap display
            if (className?.includes('language-roadmap')) {
              try {
                const rawText = typeof children === 'string'
                  ? children
                  : Array.isArray(children)
                    ? children.map(c => (typeof c === 'string' ? c : '')).join('')
                    : String(children ?? '')
                const roadmapData = JSON.parse(rawText.trim())
                return (
                  <Box sx={{ my: 1.5 }}>
                    <AIRoadmapResponse roadmap={roadmapData} compact />
                  </Box>
                )
              } catch {
                // Fall through to generic code block on parse failure
              }
            }

            const isBlock = className?.includes('language-')
            if (isBlock) {
              return (
                <Box
                  component="code"
                  sx={{
                    display: 'block',
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 1.5,
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    fontSize: '0.78rem',
                    overflowX: 'auto',
                    whiteSpace: 'pre',
                    lineHeight: 1.6,
                    textAlign: 'left',
                  }}
                >
                  {children}
                </Box>
              )
            }
            return (
              <Box
                component="code"
                sx={{
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                  borderRadius: 0.75,
                  px: 0.6,
                  py: 0.15,
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: '0.78rem',
                }}
              >
                {children}
              </Box>
            )
          },
          pre: ({ children }) => <Box sx={{ mb: 1 }}>{children}</Box>,
          a: ({ href, children }) => {
            if (href?.startsWith('/')) {
              // Deal links — render as rich card if deal is in cache
              if (href.startsWith('/deals/') && deals?.length) {
                const parts = href.split('/')
                const pk = parts[2] ? decodeURIComponent(parts[2]) : null
                const sk = parts[3] ? decodeURIComponent(parts[3]) : null
                const deal = pk && sk ? deals.find(d => d?.PK === pk && d?.SK === sk) : null

                if (deal) {
                  const org = organizations?.find(o => o?.PK === deal.PK)
                  const handleClick = () => (onNavigate ? onNavigate(href) : navigate(href))
                  return <DealCard deal={deal} orgName={org?.name} onClick={handleClick} />
                }
              }

              // Other internal links — standalone broadcasts, inline uses RouterLink
              if (onNavigate) {
                return (
                  <MuiLink
                    component="a"
                    href={href}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      onNavigate(href)
                    }}
                    sx={{
                      color: theme.palette.background?.['highlight'] || 'primary.main',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline', opacity: 0.85 },
                    }}
                  >
                    {children}
                  </MuiLink>
                )
              }
              return (
                <MuiLink
                  component={RouterLink}
                  to={href}
                  sx={{
                    color: theme.palette.background?.['highlight'] || 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline', opacity: 0.85 },
                  }}
                >
                  {children}
                </MuiLink>
              )
            }
            return (
              <MuiLink
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: theme.palette.background?.['highlight'] || 'primary.main' }}
              >
                {children}
              </MuiLink>
            )
          },
          blockquote: ({ children }) => (
            <Box
              sx={{
                borderLeft: '2px solid',
                borderColor: 'primary.main',
                pl: 1.5,
                ml: 0,
                my: 1,
                opacity: 0.8,
              }}
            >
              {children}
            </Box>
          ),
          table: ({ children }) => {
            const idx = tableRenderIndex++
            const tableData = parsedTables[idx]
            const filename = `grobotic-export-${new Date().toISOString().slice(0, 10)}`
            return (
              <Box sx={{ my: 1.5 }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      textAlign: 'left',
                      '& th, & td': {
                        px: 1.5,
                        py: 1,
                        fontSize: '0.78rem',
                        textAlign: 'left',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                      '& th': {
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                        bgcolor: alpha(theme.palette.text.primary, 0.04),
                      },
                      '& td': {
                        color: 'text.primary',
                        fontWeight: 500,
                      },
                      '& tbody tr:last-of-type td': {
                        borderBottom: 'none',
                      },
                      '& tbody tr:hover': {
                        bgcolor: alpha(theme.palette.text.primary, 0.02),
                      },
                    }}
                  >
                    {children}
                  </Box>
                </Box>
                {tableData && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<CsvIcon size={12} />}
                      onClick={() => downloadCsv(tableData, filename)}
                      sx={{
                        fontSize: '0.65rem',
                        py: 0.25,
                        px: 0.75,
                        minWidth: 0,
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      CSV
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ExcelIcon size={12} />}
                      onClick={() => downloadXlsx(tableData, filename)}
                      sx={{
                        fontSize: '0.65rem',
                        py: 0.25,
                        px: 0.75,
                        minWidth: 0,
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      }}
                    >
                      XLSX
                    </Button>
                  </Box>
                )}
              </Box>
            )
          },
          thead: ({ children }) => <thead>{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr>{children}</tr>,
          th: ({ children }) => <th>{children}</th>,
          td: ({ children }) => <td>{children}</td>,
        }}
      >
        {cleanContent}
      </Markdown>

      {/* Render extracted action buttons as chips */}
      {actions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {actions.map((btn, i) => (
            <Button
              key={i}
              size="small"
              onClick={() => onAction?.(btn.action)}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.75rem',
                borderColor: 'text.ai_agent_text',
                color: 'text.ai_agent_text',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  borderColor: 'text.highlight',
                  color: 'text.primary',
                },
              }}
            >
              {btn.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default AgentMarkdown
